import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import PrintableTicket from '../../common/PrintableTicket';

const PageContainer = styled.div`
  min-height: 100vh;
  background: #eef0f3;
  padding: 1.5rem 1rem 3rem;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media print {
    background: white;
    padding: 0;
  }
`;

const ControlBar = styled.div`
  width: 100%;
  max-width: 680px;
  margin-bottom: 1.25rem;
  display: flex;
  gap: 0.75rem;
  justify-content: center;

  @media print {
    display: none;
  }
`;

const Button = styled.button`
  padding: 0.65rem 1.5rem;
  border-radius: 8px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.18s ease, border-color 0.18s ease;
  border: 1px solid transparent;

  ${props => props.variant === 'primary' ? `
    background: #101d40;
    color: #ffffff;
    border-color: #101d40;

    &:hover {
      background: #1a2d5a;
      border-color: #1a2d5a;
    }
  ` : `
    background: #ffffff;
    color: #101d40;
    border-color: rgba(16, 29, 64, 0.25);

    &:hover {
      background: #f8f7f4;
      border-color: rgba(16, 29, 64, 0.4);
    }
  `}

  &:active {
    transform: scale(0.98);
  }
`;

// Helper function to create fallback data from URL params
const createFallbackData = (params) => {
  const dateParam = params.get('date');
  const timeParam = params.get('time');
  const ticketsParam = params.get('tickets');
  const amountParam = params.get('amount');
  const nameParam = params.get('name');
  const ref = params.get('ref');
  const ticketLinesParam = params.get('ticketLines');
  const pageIdParam = params.get('pageId');

  const ticketCount = Math.max(parseInt(ticketsParam || '1', 10) || 1, 1);
  const totalAmount = amountParam ? parseFloat(amountParam) : undefined;

  // Parse individual ticket lines if passed from the email print link
  let selectedTickets;
  if (ticketLinesParam) {
    try {
      const parsed = JSON.parse(ticketLinesParam);
      if (Array.isArray(parsed) && parsed.length > 0) {
        selectedTickets = parsed.map(t => ({
          name: t.name || 'Admission Ticket',
          quantity: parseInt(t.quantity, 10) || 1,
          price: parseFloat(t.unitPrice || t.price || 0),
          totalPrice: parseFloat(t.totalPrice || 0)
        }));
      }
    } catch (e) {
      selectedTickets = null;
    }
  }

  if (!selectedTickets) {
    const perTicket = totalAmount && ticketCount > 0 ? Number((totalAmount / ticketCount).toFixed(2)) : undefined;
    selectedTickets = [{ name: 'Admission Ticket', quantity: ticketCount, price: perTicket, totalPrice: totalAmount }];
  }

  return {
    bookingReference: ref,
    selectedTickets,
    selectedDate: dateParam ? new Date(dateParam).toISOString() : new Date().toISOString(),
    selectedTimeSlot: timeParam ? { time: timeParam } : {},
    customerDetails: nameParam ? {
      firstName: nameParam.split(' ')[0] || '',
      lastName: nameParam.split(' ').slice(1).join(' ') || ''
    } : {},
    totalAmount: totalAmount,
    venposBasket: null,
    bookingSummary: pageIdParam ? { pageId: pageIdParam } : null,
    isFamilySaver: false
  };
};

const TicketPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [ticketData, setTicketData] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);

  useEffect(() => {


    // Get storage key from URL
    const params = new URLSearchParams(location.search);
    const storageKey = params.get('key');


    if (storageKey) {
      // Get data from localStorage
      const storedData = localStorage.getItem(storageKey);


      if (storedData) {
        try {
          const parsed = JSON.parse(storedData);


          // Ensure selectedDate is valid
          if (parsed.selectedDate) {

            setTicketData(parsed);
          } else {

            setTicketData(null);
          }
        } catch (e) {


          setTicketData(null);
        }
      } else {


        setTicketData(null);
      }
    } else {

      // Try to fetch booking data from WordPress by booking reference
      const ref = params.get('ref');

      if (ref) {

        // Determine WordPress API endpoint based on environment
        const hostname = window.location.hostname;
        let apiEndpoint;
        
        if (hostname === 'localhost' || hostname === '127.0.0.1') {
          apiEndpoint = 'http://localhost:8888/Website/wordpress/wp-json';
        } else if (hostname.includes('staging.belvoircastle.com')) {
          apiEndpoint = 'https://staging.belvoircastle.com/wp/wp-json';
        } else {
          apiEndpoint = 'https://www.belvoircastle.com/wp/wp-json';
        }
        
        fetch(`${apiEndpoint}/custom/v1/get-booking-data/${ref}`)
          .then(response => response.json())
          .then(data => {
            if (data.success && data.data) {

              // Ensure selectedDate is a Date object
              if (data.data.selectedDate && typeof data.data.selectedDate === 'string') {
                data.data.selectedDate = data.data.selectedDate; // Keep as ISO string for PrintableTicket
              }
              
              setTicketData(data.data);
              setIsLoading(false);
            } else {

              setTicketData(createFallbackData(params));
              setIsLoading(false);
            }
          })
          .catch(error => {

            setTicketData(createFallbackData(params));
            setIsLoading(false);
          });
        
        // Don't set loading to false here - wait for the fetch
        return;
      } else {

        setTicketData(null);
      }
    }
    
    setIsLoading(false);
  }, [location.search]);

  const handlePrint = () => {
    window.print();
  };

  const handleClose = () => {
    window.close();
  };

  if (isLoading) {
    return (
      <PageContainer>
        <div style={{ textAlign: 'center', padding: '3rem', color: '#101d40', fontFamily: 'system-ui, sans-serif' }}>
          <p style={{ fontSize: '1rem', color: 'rgba(16,29,64,0.55)', margin: 0 }}>Loading your ticket…</p>
        </div>
      </PageContainer>
    );
  }

  if (!ticketData) {
    return (
      <PageContainer>
        <div style={{ textAlign: 'center', padding: '3rem', color: '#101d40', fontFamily: 'system-ui, sans-serif' }}>
          <h2 style={{ fontFamily: "Georgia, serif", fontWeight: 600, marginBottom: '0.75rem' }}>Ticket not found</h2>
          <p style={{ color: 'rgba(16,29,64,0.55)', marginBottom: '1.5rem' }}>
            We couldn't find the ticket data. Please return to your booking confirmation and try again.
          </p>
          <Button onClick={handleClose}>Close Window</Button>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <ControlBar>
        <Button variant="primary" onClick={handlePrint}>
          Print ticket
        </Button>
        <Button onClick={handleClose}>
          Close
        </Button>
      </ControlBar>

      {ticketData && ticketData.selectedDate && (
        <PrintableTicket
          bookingReference={ticketData.bookingReference}
          selectedTickets={ticketData.selectedTickets}
          selectedDate={new Date(ticketData.selectedDate)}
          selectedTimeSlot={ticketData.selectedTimeSlot}
          customerDetails={ticketData.customerDetails}
          totalAmount={ticketData.totalAmount}
          venposBasket={ticketData.venposBasket}
          bookingSummary={ticketData.bookingSummary}
          isFamilySaver={ticketData.isFamilySaver}
        />
      )}
    </PageContainer>
  );
};

export default TicketPage;

